import React, { cloneElement, ReactElement, useRef } from 'react';
import uniqueId from 'lodash/uniqueId';
import styled from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { Box, BoxProps } from '../layout/Box';
import { Flex, FlexProps } from '../layout/Flex';
import { Typography } from '../typography/Typography';

const Label = styled(Typography).withConfig({
    displayName: 'Label',
    shouldForwardProp: shouldIgnoreProp('required'),
})`
    user-select: none;
`;

export enum LabelPlacement {
    LEFT = 'left',
    RIGHT = 'right',
}

function getLabelWrapperProps(labelPlacement: LabelPlacement): Partial<FlexProps> | undefined {
    if (labelPlacement === LabelPlacement.LEFT) {
        return { direction: 'row', alignItems: 'center', justifyContent: 'space-between' };
    }

    if (labelPlacement === LabelPlacement.RIGHT) {
        return {
            direction: 'row-reverse',
            alignItems: 'center',
            justifyContent: 'left',
        };
    }
}

type FormControlLabelProps = Partial<BoxProps> & {
    label: ReactElement | string;
    labelPlacement?: LabelPlacement;
    control: ReactElement;
    labelVerticalPadding?: number;
    disabled?: boolean;
};

export const FormControlLabel = (props: FormControlLabelProps) => {
    const {
        label,
        borderBottom,
        labelVerticalPadding = 2,
        labelPlacement = LabelPlacement.RIGHT,
        control,
        ...other
    } = props;
    const labelIdRef = useRef(uniqueId('form-field-'));

    return (
        <Box width="100%" {...other}>
            <Flex flexGrow={1} spacing={2} {...getLabelWrapperProps(labelPlacement)}>
                {label && (
                    <Label
                        as="label"
                        htmlFor={labelIdRef.current}
                        variant="body"
                        color="primary"
                        width={labelPlacement === LabelPlacement.RIGHT ? '100%' : 'auto'}
                        py={labelVerticalPadding}
                        borderBottom={borderBottom}
                    >
                        {label}
                    </Label>
                )}
                {cloneElement(control, {
                    ...props,
                    id: labelIdRef.current,
                })}
            </Flex>
        </Box>
    );
};
